// Add a local interface definition
interface TypeAccordionItemData {
  id: string;
  name: string;
  balance: number;
  type?: string;
  subType?: string;
  children?: TypeAccordionItemData[];
}

import { FilledArrow } from "../../../assets";
import { Taxes } from "../../../providers/ReportsProvider";
import { formatUniversalNumber } from "../../../lib/utils";
import { StringOverrides } from "..";
import { ProfitRows } from "./ProfitRows";
import { AnimatedAccordionContent } from "./AnimatedAccordionContent";

interface AccordionItemsRendererProps {
  items: TypeAccordionItemData[];
  tabId: string;
  isExpanded: (tabId: string, itemId: string) => boolean;
  toggleAccordion: (tabId: string, itemId: string) => void;
  level?: number;
  taxes?: Taxes;
  stringOverrides?: StringOverrides;
}

const AccordionItemsRenderer = ({
  items,
  tabId,
  isExpanded,
  toggleAccordion,
  level = 0,
  taxes = {
    amount: 0,
    displayName: "",
    name: "",
  },
  stringOverrides = {
    profitBeforeTaxesLabel: "Profit Before Taxes",
    netProfitLabel: "Net Profit",
  },
}: AccordionItemsRendererProps) => {
  if (level === 0 && tabId === "profitAndLoss") {
    // For Profit & Loss, create three separate accordion components
    const revenueItems = items.filter((item) => item.id === "revenue"); // Revenue, COGS, Gross Profit
    const expensesItems = items.filter((item) => item.id === "expense");

    // const grossProfitValue = revenueItems.reduce((sum, item) => {
    //   return sum + item.balance;
    // }, 0);

    // const grossProfitItem = {
    //   id: "gross-profit",
    //   name: stringOverrides.grossProfitLabel,
    //   balance: grossProfitValue,
    //   type: "GROSS_PROFIT",
    //   children: [],
    // };
    const revenueItemsWithGrossProfit = [...revenueItems];
    const totalRevenue = revenueItems[0] ? revenueItems[0].balance : 0;
    const totalExpense = expensesItems[0] ? expensesItems[0].balance : 0;

    const profitBeforeTax = totalRevenue - totalExpense;
    const tax = taxes?.amount || 0;
    const netProfit = profitBeforeTax - tax;

    const profitItems = [
      {
        id: "profit-before-taxes",
        name: stringOverrides.profitBeforeTaxesLabel,
        balance: `${formatUniversalNumber(profitBeforeTax)}`,
        type: "PROFIT",
        children: [],
      },
      {
        id: "taxes",
        name: "Taxes",
        balance: `${formatUniversalNumber(tax)}`,
        type: "TAX",
        children: [],
      },
      {
        id: "net-profit",
        name: stringOverrides.netProfitLabel,
        balance: `${formatUniversalNumber(netProfit)}`,
        type: "NET_PROFIT",
        children: [],
      },
    ];

    return (
      <div className="flex flex-col space-y-4">
        {/* First Section: Revenue & Profit */}
        {revenueItems?.length > 0 && (
          <div className="border BookEZ_accordion_bg BookEZ_accordion_border_color BookEZ_font_body_size BookEZ_card_radius BookEZ_font_color_gray_2 overflow-hidden">
            {renderSection(
              revenueItemsWithGrossProfit,
              tabId,
              isExpanded,
              toggleAccordion,
              level,
              "gross-profit"
            )}
          </div>
        )}

        {/* Second Section: Operating Expenses */}
        {expensesItems?.length > 0 && (
          <div className="border BookEZ_accordion_bg BookEZ_accordion_border_color BookEZ_font_body_size BookEZ_card_radius BookEZ_font_color_gray_2 overflow-hidden">
            {renderSection(
              expensesItems,
              tabId,
              isExpanded,
              toggleAccordion,
              level
            )}
          </div>
        )}

        {/* Third Section: Profit Calculation */}
        {revenueItems?.length > 0 && <ProfitRows profitItems={profitItems} />}
      </div>
    );
  }

  // For Balance Sheet, create three separate accordion components
  if (level === 0 && tabId === "balanceSheet") {
    const assetsItems = items.filter((item) => item.id === "asset"); // Assets
    const liability = items.filter((item) => item.id === "liability"); // Secondary Assets
    const liabilitiesEquityItems = items.slice(2, 3); // Liabilities & Equity

    return (
      <div className="flex flex-col space-y-4">
        {/* First Section: Assets */}
        {assetsItems?.length > 0 && (
          <div className="border BookEZ_card_border_color BookEZ_card_radius overflow-hidden">
            {renderSection(
              assetsItems,
              tabId,
              isExpanded,
              toggleAccordion,
              level,
              "assets"
            )}
          </div>
        )}

        {/* Second Section: Secondary Assets */}
        {liability?.length > 0 && (
          <div className="border BookEZ_card_border_color BookEZ_card_radius overflow-hidden">
            {renderSection(
              liability,
              tabId,
              isExpanded,
              toggleAccordion,
              level,
              "secondaryAssets"
            )}
          </div>
        )}
      </div>
    );
  }

  // Default rendering for other tabs
  if (level === 0) {
    return (
      <div className="border BookEZ_card_border_color BookEZ_card_radius overflow-hidden">
        {renderSection(items, tabId, isExpanded, toggleAccordion, level)}
      </div>
    );
  }

  return items.map((item, index) =>
    renderItem(item, index === items.length - 1, index === 0, level)
  );

  function renderItem(
    item: TypeAccordionItemData,
    isLast: boolean,
    isFirst: boolean,
    level: number = 0
  ) {
    const expanded = isExpanded(tabId, item.id);
    const hasChildren = item.children && item.children.length > 0;

    // Special styling for different item types
    const isProfitBeforeTaxes =
      item.name === stringOverrides.profitBeforeTaxesLabel;
    const isNetProfit = item.name === stringOverrides.netProfitLabel;

    // Item type flags
    const isTotal = isNetProfit || isProfitBeforeTaxes;

    // Background colors
    let bgColorClass = "BookEZ_accordion_bg";
    if (isNetProfit) {
      bgColorClass = "BookEZ_bg_green_light";
    } else if (isTotal) {
      bgColorClass = "bg-gray-50";
    }

    // For expandable items that are expanded
    if (expanded && hasChildren) {
      bgColorClass = "BookEZ_accordion_bg_active";
    }

    // Calculate padding based on level
    const paddingLeft = 14 + level * 10; // Base padding of 14px plus 10px per level

    // Set text color for children
    const textColorClass =
      level > 0 ? "BookEZ_font_color_gray_2" : "BookEZ_font_color_gray_2";
    const fontWeightColorClass =
      level > 0 ? "BookEZ_font_light_bold" : "BookEZ_font_bold";

    return (
      <div
        key={item.id}
        className={`${bgColorClass} ${
          !isLast ? "border-b BookEZ_card_border_color" : ""
        }`}
      >
        <div className="group relative">
          <div
            onClick={() => hasChildren && toggleAccordion(tabId, item.id)}
            className={`py-3 ${
              hasChildren ? "cursor-pointer" : ""
            } w-full text-left ${isTotal ? "BookEZ_font_bold" : ""} ${
              isNetProfit ? "hover:bg-[#bbd1c3]/80" : ""
            } transition-colors duration-200 ease-in`}
          >
            <div className="flex items-center w-full">
              {/* Chevron */}
              <div className="w-6 flex justify-center mx-4">
                {hasChildren && (
                  <span className="BookEZ_font_color_gray_2 transition-transform duration-300">
                    {expanded ? (
                      <FilledArrow />
                    ) : (
                      <FilledArrow className="-rotate-90" />
                    )}
                  </span>
                )}
              </div>

              {/* Item Content */}
              <div
                className={`flex justify-between w-full BookEZ_font_body_size ${fontWeightColorClass} ${textColorClass}`}
                style={{ paddingLeft: `${paddingLeft}px` }}
              >
                <div>{item.name}</div>
                <div
                  className={`pr-4 ${
                    isNetProfit ? "BookEZ_accordion_net_profit_text_color" : ""
                  }`}
                >
                  {formatUniversalNumber(item.balance)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recursively render children if expanded */}
        {hasChildren && (
          <AnimatedAccordionContent isOpen={expanded}>
            <div>
              {item.children?.map(
                (child: TypeAccordionItemData, index: number) => (
                  <div key={child.id}>
                    {renderItem(
                      child,
                      index === item.children!.length - 1,
                      index === 0,
                      level + 1
                    )}
                  </div>
                )
              )}
            </div>
          </AnimatedAccordionContent>
        )}
      </div>
    );
  }

  function renderSection(
    items: TypeAccordionItemData[],
    tabId: string,
    isExpanded: any,
    toggleAccordion: any,
    level: number,
    highlightId?: string
  ) {
    return items.map((item, index) => {
      const isLast = index === items.length - 1;
      const hasChildren = item.children && item.children.length > 0;
      const expanded = isExpanded(tabId, item.id);
      const isHighlighted = highlightId && item.id === highlightId;

      let bgColorClass = "BookEZ_accordion_bg";
      if (expanded && hasChildren) bgColorClass = "BookEZ_accordion_bg_active";
      if (isHighlighted) bgColorClass = "BookEZ_accordion_gross_profit_bg"; // or any highlight

      return (
        <div
          key={item.id}
          className={`${bgColorClass} ${
            !isLast ? "border-b BookEZ_card_border_color" : ""
          }`}
        >
          <div className="group relative">
            <div
              onClick={() => hasChildren && toggleAccordion(tabId, item.id)}
              className={`py-3 ${
                hasChildren ? "cursor-pointer" : ""
              } w-full text-left ${isHighlighted ? "BookEZ_font_bold" : ""} ${
                isHighlighted ? "" : ""
              } transition-colors duration-200 ease-in`}
            >
              <div className="flex items-center w-full">
                {/* Chevron */}
                <div className="w-6 flex justify-center mx-4">
                  {hasChildren && (
                    <span className="BookEZ_font_color_gray_2 transition-transform duration-300">
                      {expanded ? (
                        <FilledArrow />
                      ) : (
                        <FilledArrow className="-rotate-90" />
                      )}
                    </span>
                  )}
                </div>

                {/* Item Content */}
                <div
                  className="flex justify-between w-full BookEZ_font_body_size BookEZ_font_bold BookEZ_font_color_gray_3"
                  style={{ paddingLeft: `${14 + level * 10}px` }}
                >
                  <div>{item.name}</div>
                  <div
                    className={`pr-4 ${
                      isHighlighted
                        ? "BookEZ_accordion_gross_profit_text_color"
                        : ""
                    }`}
                  >
                    {formatUniversalNumber(item.balance)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recursively render children if expanded */}
          {hasChildren && (
            <AnimatedAccordionContent isOpen={expanded}>
              <div>
                {item.children?.map(
                  (child: TypeAccordionItemData, index: number) => (
                    <div key={child.id}>
                      {renderItem(
                        child,
                        index === item.children!.length - 1,
                        index === 0,
                        level + 1
                      )}
                    </div>
                  )
                )}
              </div>
            </AnimatedAccordionContent>
          )}
        </div>
      );
    });
  }
};
export default AccordionItemsRenderer;
